import type { JSX } from 'react'
import type { ILabelProps } from '../Label'
import type { Backgrounds, Padding } from '../Label.h'

import { COLOR } from 'vars/index'
import { Label } from '../Label'
import {
  Backgrounds as LabelBackgrounds,
  Corners,
  Fonts,
  Paddings,
} from '../Label.h'

/**
 * Tone of the status pill above the title of a details `banner-description`.
 * A new state adds a tone here plus its background below.
 */
export const StatusBadgeTone = {
  /** Airing right now. */
  ACCENT: 'ACCENT',
  /** Scheduled to air later, such as an event that has not started yet. */
  INFO: 'INFO',
} as const
export type StatusBadgeTone =
  (typeof StatusBadgeTone)[keyof typeof StatusBadgeTone]

export const BACKGROUND_BY_TONE: Record<StatusBadgeTone, Backgrounds> = {
  [StatusBadgeTone.ACCENT]: LabelBackgrounds.ADDITIONAL_RED_3,
  // Upcoming frames use subtitle/blue `#0047ff`; the token lives in the
  // player-subtitle palette, but the value is the one the concept binds.
  [StatusBadgeTone.INFO]: LabelBackgrounds.SUBTITLE_BLUE,
}

/** Both live and upcoming labels are 8px / 2px from the Figma labels component. */
export const PADDING_BY_TONE: Record<StatusBadgeTone, Padding> = {
  [StatusBadgeTone.ACCENT]: Paddings.SIZE_90,
  [StatusBadgeTone.INFO]: Paddings.SIZE_90,
}

export interface ILabelStatusBadgeProps extends ILabelProps {
  tone?: StatusBadgeTone
}

/**
 * Status label from the content details concept (`labels` type live / upcoming).
 * Live and Upcoming are text-only — Figma reserves icons for paid and shorts.
 */
export const LabelStatusBadge = ({
  tone = StatusBadgeTone.ACCENT,
  background = BACKGROUND_BY_TONE[tone],
  color = COLOR.TEXT_ICON_CONSTANT_LIGHT,
  corners = Corners.CORNERS_10,
  font = Fonts.FontXsBold,
  padding = PADDING_BY_TONE[tone],
  ...rest
}: ILabelStatusBadgeProps): JSX.Element => (
  <Label
    background={background}
    color={color}
    corners={corners}
    font={font}
    padding={padding}
    {...rest}
  />
)
